import { useCallback, useEffect, useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import {
  connectGitHub,
  disconnectGitHub,
  getGitHubStatus,
  type GitHubConnection,
} from '@/services/github-api'
import { useStudioLocale } from '@/i18n/use-studio-locale'

export function GitHubConnectSection() {
  const { strings } = useStudioLocale()
  const copy = strings.settings.github
  const user = useStudioStore((s) => s.user)
  const [status, setStatus] = useState<GitHubConnection>({ connected: false })
  const [loading, setLoading] = useState(false)

  // Fetch connection status on mount
  useEffect(() => {
    if (!user?.id) return
    getGitHubStatus(user.id).then(setStatus)
  }, [user?.id])

  const handleConnect = useCallback(async () => {
    setLoading(true)
    try {
      const username = await connectGitHub()
      setStatus({ connected: true, username })
    } catch (err) {
      console.error('[GitHubConnectSection] Connect failed:', err)
      // Refresh status in case connection succeeded but popup signalling failed
      if (user?.id) {
        const refreshed = await getGitHubStatus(user.id)
        setStatus(refreshed)
      }
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const handleDisconnect = useCallback(async () => {
    if (!user?.id) return
    if (!window.confirm(copy.confirmDisconnect)) {
      return
    }
    setLoading(true)
    try {
      await disconnectGitHub(user.id)
      setStatus({ connected: false })
    } catch (err) {
      console.error('[GitHubConnectSection] Disconnect failed:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  return (
    <section className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">{copy.title}</h2>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {copy.developer}
        </span>
      </div>

      {status.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm">Connected as <span className="font-medium">@{status.username}</span></span>
            <StatusBadge status="active" label={copy.connected} />
          </div>
          <Button variant="outline" size="lg" className="px-3 text-sm" disabled={loading} onClick={handleDisconnect}>
            {loading ? copy.disconnecting : copy.disconnect}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {copy.invite}
          </p>
          <Button variant="outline" size="lg" className="px-3 text-sm" disabled={loading} onClick={handleConnect}>
            {loading ? copy.connecting : copy.connect}
          </Button>
        </div>
      )}
    </section>
  )
}
